const ONE_MEGABYTE_AS_INT = 1_000_000;

export const formatNumber = (number: number, locale = 'en-us') =>
  new Intl.NumberFormat(locale).format(number);

/**
 * Return number value for a file size in MB. This format is specific
 * to reporting file sizes in Segment.
 * <1 Kb shows up to 5 decimals
 * >=1 Kb and < 1MB shows up to 2 decimals
 * >= 1MB rounds to nearest whole number
 *
 * @example
 * ```ts
 * import { formatFileSizeInMb } from '@frameio/utils/formatters';
 * const bytes = 88386283;
 * const fileSizeDisplay = formatFileSizeInMb(bytes); // results in 88
 * ```
 */

export const formatFileSizeInMB = (fileSize: number): number => {
  if (!fileSize && fileSize !== 0) {
    return NaN;
  }
  if (fileSize === Infinity) return NaN;

  const fileSizeMB = fileSize / ONE_MEGABYTE_AS_INT;
  let minFractionDigits: number | undefined;
  let maxFractionDigits: number | undefined;

  if (fileSizeMB < 0.001) {
    minFractionDigits = 1;
    maxFractionDigits = 5;
  } else if (fileSizeMB >= 0.001 && fileSizeMB < 1) {
    minFractionDigits = 1;
    maxFractionDigits = 2;
  }
  if (minFractionDigits != null && maxFractionDigits != null) {
    const formatter = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: minFractionDigits,
      maximumFractionDigits: maxFractionDigits,
    });
    const fileSizeSignificantDig = formatter.format(fileSizeMB);
    return Number(fileSizeSignificantDig);
  }
  return Math.round(fileSizeMB);
};
